import React, {useEffect, useState, useRef, useMemo, useCallback} from 'react';
import {
  Table,
  TableContainer,
  Paper,
  Box,
  Typography,
  Checkbox,
} from '@mui/material';
import TableHeader from './tableHeader';
import {useDispatch, useSelector} from 'react-redux';
import './table.scss';
import TableBodyRows from './tableBody';
import {
  clearAttendanceByDateData,
  deleteAttendance,
  getNewAttendance,
  saveSessionForAttendance,
} from '../../../../../../../redux/risk-assessment-core/riskAssessmentCoreSlice';
import SessionModal from '../modal/sessionModal';
import {useLocation} from 'react-router-dom';
import {getFormattedTime, hoursToHoursMinutes} from '../../../../utils/helpers';
import {getLoginStateSelector} from '../../../../../../../redux/selectors/loginSelector';
import CustomPopover from '../../../../../../../components/custom-popover/customPopver';
import {assets} from '../../../../../../../utils/assetsSrc';
import Confirmation from '../../../../../../../components/confirmation/confirmation';

const AttendanceTable = ({data = {}, riskAssessmentUsers = []}) => {
  const [rows, setRows] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedUrls, setLoadedUrls] = useState(new Set());

  const tableContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const location = useLocation();

  const raData = location?.state?.selectedRiskAssessment;

  const riskAssessmentId = raData?.id;
 

  const loginState = useSelector(getLoginStateSelector());
  const loggedInId = loginState?.loggedInUser?.id;

  const [selectedAttendance, setSelectedAttendance] = useState(null);



  const [popoverOpen, setPopoverOpen] = useState(null);
  const [selectedData, setSelectedData] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  // Initialize data
  useEffect(() => {
    if (data.results) {
      setRows(data.results);

      
      setNextPage(data.next);
      setPrevPage(data.previous);
      // Track the initial data as loaded
      setLoadedUrls(new Set(['initial']));
    }
  }, [data]);

  const toggleEdit = () => {
    setIsEditing(prev => !prev);
  };

  // SSRF prevention: whitelist of allowed origins for pagination fetch (same-origin or known API base only)
  const ALLOWED_FETCH_ORIGINS = useMemo(() => {
    const origins = [window.location.origin];
    if (window.location.hostname === 'localhost') {
      origins.push('http://localhost:8000');
    }
    return origins;
  }, []);

  const fetchPage = useCallback(
    async (url, isPrev) => {
      if (!url || loading) return;

      // Use URL as unique identifier to prevent duplicate fetches
      if (loadedUrls.has(url)) {
        return;
      }

      // SSRF prevention: validate URL against whitelist before passing to fetch (CWE-918)
      let urlToFetch = null;
      try {
        const parsed = new URL(url);
        if (ALLOWED_FETCH_ORIGINS.includes(parsed.origin)) {
          urlToFetch = url;
        }
      } catch {
        // Invalid URL - reject
      }
      if (!urlToFetch) {
        return;
      }

      setLoading(true);

      fetch(urlToFetch)
      .then(response => response.json())
      .then(newData => {
        setRows(prev => {
          if (isPrev) {
            return [...newData.results, ...prev];
          } else {
            return [...prev, ...newData.results];
          }
        });

        setNextPage(newData.next);
        setPrevPage(newData.previous);
        setLoadedUrls(prev => new Set([...prev, urlToFetch]));
      })
      .catch(error => {
        console.error('Error fetching data:', error);
      })
      .finally(() => {
        setLoading(false);
      });
    },
    [loading, loadedUrls, ALLOWED_FETCH_ORIGINS]
  );


  // Infinite scroll detection with debouncing
  const handleScroll = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container || loading || !nextPage) return;

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Debounce scroll events
    scrollTimeoutRef.current = setTimeout(() => {
      const { scrollTop, clientHeight, scrollHeight } = container;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      
      // Load more when user is 90% down the page
      if (scrollPercentage >= 0.9) {
        fetchPage(nextPage, false);
      }
    }, 150); // 150ms debounce
  }, [loading, nextPage, fetchPage]);

  // Add scroll listener
  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
        // Cleanup timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, [handleScroll]);

  const handleModalClose = fromSave => {
    dispatch(clearAttendanceByDateData());
    setIsModalOpen(false);
    setSelectedData(null);
    if (fromSave) dispatch(getNewAttendance({riskAssessmentId}));
  };

  const handleAddUserSession = session => {
    const {date, time, user, key} = session;

    if (!date || !time || !user || !key) {
      return;
    }
    const [month, day, year] = date.split('/');
    const localDate = new Date(Number(year), Number(month) - 1, Number(day));

    const formattedDate = `${localDate.getFullYear()}-${String(
      localDate.getMonth() + 1,
    ).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
    const payload = {
      session_id: key,
      date: formattedDate,
      duration: Number(time) * 60,
      name: [user],
      employee_id: loggedInId,
      logged_by_id: loggedInId,
      riskAssessmentId,
    };

    dispatch(
      saveSessionForAttendance({
        payload,
        callback: result => {
          handleModalClose(true);
        },
      }),
    );
  };

  const handleDeleteUserSession = async session => {
    const userId = session?.user?.id;

    const attendee = session.attendance?.find(
      user => user?.employee_id === userId,
    );

    const attendeeId = attendee?.id;
    dispatch(
      deleteAttendance({
        attendance_id: attendeeId,
        callback: () => handleModalClose(true),
      }),
    );
    // // setRows((prev) => prev.filter((item, i) => item?.attendee?.id !== id));
    // const updatedRows = [...rows];

    // const updatedRow = {...updatedRows[index]};

    // updatedRow.attendance = updatedRow.attendance?.filter(
    //   item => item?.id !== id,
    // );

    // updatedRow.time = updatedRow.attendance?.reduce(
    //   (acc, curr) => curr.time + acc,
    //   0,
    // );

    // // Update the rows state with the modified row
    // updatedRows[index] = updatedRow;
    // setRows(updatedRows);
  };

  const handleDeleteAttendanceRecord = () => {
    if (!selectedData) {
      return;
    }
    const {key} = selectedData;
    dispatch(
      deleteAttendance({
        id: key,
        callback: () => {handleModalClose(true); setConfirmDelete(false); },
      }),
    );
  };

  const handleCheckboxChange = (session, checked) => {
    if (checked) {
      handleAddUserSession(session);
    } else {
      handleDeleteUserSession(session);
    }
  };



  // Simple sorting for better performance
  const sortedRiskAssessmentUsers = useMemo(() => {
    return riskAssessmentUsers?.sort((a,b) => (a?.first_name ?? "").localeCompare(b?.first_name ?? ""))
  }, [riskAssessmentUsers]);

  const tableHeaders = useMemo(() => {
    const headers = [{label: 'Session'}];
    sortedRiskAssessmentUsers?.forEach(user => {
      headers.push({label: (!user?.first_name && !user?.last_name)? user?.email:`${user?.first_name ?? "N/A"} ${user?.last_name ?? ""}`});
    });
    return headers;
  }, [sortedRiskAssessmentUsers]);

  const handleOpenPopver = useCallback((e, session) => {
    setSelectedData(session);
    setPopoverOpen(e.currentTarget);
  }, []);

  const handleClosePopver = useCallback(() => {
    setPopoverOpen(null);
  }, []);

  // Highly optimized table rows with better memoization and performance
  const tableRows = useMemo(() => {
    if (!rows?.length) return [];
    
    return rows.map((row, index) => {
      return [
        <div
          key={`session-${index}`}
          onClick={e => handleOpenPopver(e, row)}
          style={{
            display: 'flex', 
            flexDirection: 'column', 
            cursor: 'pointer',
            minHeight: '60px',
            justifyContent: 'center',
            padding: '8px'
          }}>
          <span style={{ fontWeight: '600', fontSize: '14px' }}>{row?.date}</span>
          <span style={{ fontSize: '12px', color: '#666' }}>{getFormattedTime(row.time)}</span>
        </div>,
        ...riskAssessmentUsers?.map((user, userIndex) => {
          const isChecked = row?.attendance?.some(
            att => att?.employee_id === user?.id,
          );
          
          return (
            <Checkbox
              key={`user-${userIndex}-${index}`}
              onChange={e =>
                handleCheckboxChange({...row, user}, e.target.checked)
              }
              checked={isChecked}
              sx={{
                '&.Mui-checked': {
                  color: 'var(--primary-blue-color)',
                },
              }}
            />
          );
        }),
      ];
    });
  }, [rows, riskAssessmentUsers, handleOpenPopver]);

  return (
    <Box sx={{display: 'flex', flexDirection: 'column'}}>
        {/* <Typography
          variant="h6"
          style={{alignSelf: 'end'}}
          onClick={toggleEdit}
          sx={{
            textDecoration: 'underline',
            textUnderlineOffset: 4,
            cursor: 'pointer',
          }}>
          {isEditing ? 'Close' : 'Edit'}
        </Typography> */}
        
        {riskAssessmentUsers?.length > 0 && (
            <TableContainer
              elevation={0}
              sx={{
                flex: 1,
                maxHeight: '500px',
                minHeight: '400px',
                overflowY: 'scroll',
                overflowX: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                position: 'relative',
                // Performance optimizations
                willChange: 'scroll-position',
                contain: 'layout style paint',
                // Enhanced scrollbar styling
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f5f5f5',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#a8a8a8',
                  },
                },
                // Smooth scrolling with performance
                scrollBehavior: 'smooth',
                // Optimize rendering
                transform: 'translateZ(0)', // Force GPU acceleration
              }}
              ref={tableContainerRef}
              className="table-attendance">
              <Table stickyHeader sx={{border: 'none'}}>
                <TableHeader headers={tableHeaders} />
                <TableBodyRows rows={tableRows} />
              </Table>
              
       
            </TableContainer>
        )}

      {riskAssessmentUsers?.length<=0 &&(
        <Typography sx={{textAlign: 'center', marginTop: 2}}>
          No Data Found!
        </Typography>
      ) }

             {/* Loading indicator inside table */}
             {loading && nextPage && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e9ecef'
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}>
                    <Box sx={{
                      width: 20,
                      height: 20,
                      border: '2px solid #e3f2fd',
                      borderTop: '2px solid var(--primary-blue-color)',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }} />
                    <Typography sx={{ 
                      color: 'var(--primary-blue-color)',
                      fontWeight: '500',
                      fontSize: '14px'
                    }}>
                      Loading more attendance records...
                    </Typography>
                  </Box>
                </Box>
              )}


      {isModalOpen && (
        <SessionModal
          isEditModal
          selectedAttendance={selectedData}
          open={isModalOpen}
          onClose={handleModalClose}
        />
      )}

      {popoverOpen && (
        <CustomPopover open={popoverOpen} handleClose={handleClosePopver}>
          <div className="user-actions">
            <img onClick={()=>{handleClosePopver(); setIsModalOpen(true)}} className="edit" src={assets.editIcon} />
            <img
              onClick={() =>{handleClosePopver(); setConfirmDelete(true); }}
              className="delete"
              src={assets.deleteIcon}
            />
          </div>
        </CustomPopover>
      )}

      <Confirmation
        label={'Delete Session'}
        show={confirmDelete}
        setShow={setConfirmDelete}
        confirmationType={'typeDelete'}
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        primaryButtonClickHandler={handleDeleteAttendanceRecord}
        secondaryButtonClickHandler={() => {
          setConfirmDelete(false);
        }}
      />
    </Box>
  );
};

export default AttendanceTable;
